'use client';

import Image from 'next/image';
import { CalendarClock, ImagePlus, Music, UploadCloud, UserCheck, type LucideIcon } from 'lucide-react';
import { OCCASIONS, useHeroOccasion, type FeatureKey } from './HeroOccasion';

// Fan slots (% of the collage box). FRONT is where the featured card sits.
const SLOTS = {
  back: { left: '0%', top: '16%', rotate: -10, scale: 0.94, z: 0 },
  previous: { left: '21%', top: '6%', rotate: -3, scale: 0.97, z: 10 },
  front: { left: '43%', top: '0%', rotate: 3, scale: 1.04, z: 20 },
  next: { left: '64%', top: '14%', rotate: 10, scale: 0.97, z: 10 },
} as const;

// Product features floating around the fan. The one matching the front card lights up.
const FEATURES: { key: FeatureKey; icon: LucideIcon; label: string; className: string; delay: string }[] = [
  { key: 'photos', icon: ImagePlus, label: 'Photos & GIFs', className: '-top-3 left-[5%]', delay: '0s' },
  { key: 'music', icon: Music, label: 'Music', className: 'top-[3%] right-[1%]', delay: '-1.2s' },
  { key: 'logo', icon: UploadCloud, label: 'Logo upload', className: 'top-[46%] -left-3 hidden sm:flex', delay: '-2.1s' },
  { key: 'signup', icon: UserCheck, label: 'No signup to sign', className: 'bottom-[13%] left-[3%]', delay: '-0.6s' },
  { key: 'schedule', icon: CalendarClock, label: 'Scheduled delivery', className: 'bottom-[-2%] left-[30%] hidden sm:flex', delay: '-1.7s' },
];

/** Animated hero card fan: cards rotate through the front slot (birthday → work
 *  anniversary → farewell → thank you); the signature bubble and the matching
 *  feature chip follow. Timer lives in HeroOccasionProvider (shared with the
 *  handwritten headline word). Pauses on hover; still for reduced motion. */
export default function HeroCardFan() {
  const { index: front, setPaused } = useHeroOccasion();
  const n = OCCASIONS.length;
  const slotFor = (i: number) => [SLOTS.front, SLOTS.next, SLOTS.back, SLOTS.previous][(i - front + n) % n];
  const current = OCCASIONS[front];

  return (
    <div className="relative aspect-[5/4] w-full" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
      {OCCASIONS.map((o, i) => {
        const s = slotFor(i);
        const isFront = i === front;
        return (
          <div
            key={o.card.src}
            className="absolute w-[36%] transition-all duration-700 ease-[cubic-bezier(.22,.8,.25,1)] motion-reduce:transition-none"
            style={{ left: s.left, top: s.top, zIndex: s.z, transform: `rotate(${s.rotate}deg) scale(${s.scale})` }}
          >
            <div
              className={`overflow-hidden rounded-2xl bg-white ring-1 ring-black/5 transition-shadow duration-700 ${
                isFront ? 'hero-card-float shadow-2xl shadow-ink/30' : 'shadow-xl shadow-ink/15'
              }`}
            >
              <Image
                src={o.card.src}
                alt={o.card.alt}
                width={300}
                height={375}
                priority={i === 0}
                sizes="(min-width: 1024px) 200px, 36vw"
                className="h-auto w-full"
              />
            </div>
          </div>
        );
      })}

      {/* Signature bubble — re-keyed so it animates in for each occasion */}
      <div
        key={current.word}
        className="hero-bubble-in absolute bottom-[4%] right-[3%] z-30 max-w-[46%] rounded-2xl bg-white px-3.5 py-2.5 text-xs leading-snug text-ink shadow-lg ring-1 ring-paper-rule"
      >
        <p className="italic">“{current.message.text}”</p>
        <p className="mt-1 font-semibold">— {current.message.from}</p>
      </div>

      {FEATURES.map(({ key, icon: Icon, label, className, delay }) => {
        const active = key === current.feature;
        return (
          <div
            key={key}
            className={`hero-chip-float absolute z-30 flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold shadow-md ring-1 transition-colors duration-500 sm:gap-2 sm:px-3.5 sm:py-2 ${className} ${
              active ? 'bg-ink text-white shadow-ink/30 ring-ink' : 'bg-white text-ink shadow-ink/10 ring-paper-rule'
            }`}
            style={{ animationDelay: delay }}
          >
            <Icon className={`h-3.5 w-3.5 sm:h-4 sm:w-4 ${active ? 'text-blush' : 'text-heart-600'}`} aria-hidden="true" />
            {label}
          </div>
        );
      })}
    </div>
  );
}
